import { Show, createResource, createSignal, type Component } from "solid-js";
import { api } from "../api";
import { formatBookingTime } from "../bookingTime";
import type { ManagedBooking } from "../types";
import { CalendarIcon, CheckIcon, GlobeIcon, VideoIcon } from "./icons";

interface Props {
  token: string;
}

const ManageBooking: Component<Props> = (props) => {
  const [booking, { refetch }] = createResource(() =>
    api<ManagedBooking>(`/api/manage/${props.token}`),
  );
  const [busy, setBusy] = createSignal(false);
  const [error, setError] = createSignal("");

  async function cancel(): Promise<void> {
    if (!window.confirm("Cancel this meeting for every participant?")) return;
    setBusy(true);
    setError("");
    try {
      await api(`/api/manage/${props.token}/cancel`, { method: "POST" });
      await refetch();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not cancel.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <main class="manage-page">
      <Show
        when={booking()}
        fallback={
          <div class="page-state">
            <div class="loading-spinner" />
            <p>{booking.loading ? "Loading booking…" : booking.error?.message}</p>
          </div>
        }
      >
        {(loaded) => (
          <section class="manage-card">
            <div
              class="status-icon"
              classList={{ cancelled: loaded().status !== "confirmed" }}
            >
              <CheckIcon />
            </div>
            <p class="eyebrow">{loaded().host_name}</p>
            <h1>
              {loaded().status === "confirmed"
                ? "Your meeting is scheduled"
                : loaded().status === "cancelled"
                  ? "Meeting cancelled"
                  : "Meeting updated"}
            </h1>
            <div class="manage-details">
              <h2>{loaded().event_title}</h2>
              <div class="summary-row">
                <CalendarIcon />
                <span>
                  {formatBookingTime(
                    loaded().start,
                    loaded().end,
                    loaded().invitee_timezone,
                  )}
                </span>
              </div>
              <div class="summary-row">
                <GlobeIcon />
                <span>{loaded().invitee_timezone.replaceAll("_", " ")}</span>
              </div>
              <Show when={loaded().meet_url && loaded().status === "confirmed"}>
                <div class="summary-row">
                  <VideoIcon />
                  <a href={loaded().meet_url}>Join Google Meet</a>
                </div>
              </Show>
            </div>
            <Show when={loaded().can_manage}>
              <div class="manage-actions">
                <a
                  class="primary-button link-button"
                  href={`/${loaded().handle}/${loaded().event_slug}?reschedule=${props.token}`}
                >
                  Reschedule
                </a>
                <button
                  class="secondary-button danger"
                  type="button"
                  disabled={busy()}
                  onClick={cancel}
                >
                  {busy() ? "Cancelling…" : "Cancel meeting"}
                </button>
              </div>
            </Show>
            <Show when={error()}>
              <p class="form-error" role="alert">
                {error()}
              </p>
            </Show>
          </section>
        )}
      </Show>
    </main>
  );
};

export default ManageBooking;
